import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, User2, Phone, Handshake, MapPin, Trophy, Briefcase,
  ClipboardList, Building2, Layers, Megaphone, HeartHandshake, CheckSquare,
  UserCog, Target, IndianRupee, BarChart3, Bell, ShieldCheck, Cog, ScrollText,
  LogOut, Search, ChevronsLeft, ChevronsRight, Upload, GitMerge, DatabaseBackup
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const NAV = [
  { group: "Core Operating", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
    { to: "/leads", label: "Leads", icon: Users, module: "leads" },
    { to: "/clients", label: "Clients", icon: User2, module: "clients" },
    { to: "/presales", label: "Pre-Sales", icon: Phone, module: "presales" },
    { to: "/cps", label: "Channel Partners", icon: Handshake, module: "cps" },
  ]},
  { group: "Operations & Site", items: [
    { to: "/visits", label: "Site Visits", icon: MapPin, module: "visits" },
    { to: "/bookings", label: "Bookings", icon: Trophy, module: "bookings" },
    { to: "/inventory", label: "Inventory", icon: Layers, module: "inventory" },
    { to: "/projects", label: "Projects", icon: Building2, module: "projects" },
  ]},
  { group: "Strategy & Performance", items: [
    { to: "/marketing", label: "Marketing", icon: Megaphone, module: "marketing" },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, module: "tasks" },
    { to: "/targets", label: "Targets", icon: Target, module: "targets" },
    { to: "/reports", label: "Reports & MIS", icon: BarChart3, module: "reports" },
  ]},
  { group: "Governance & Admin", items: [
    { to: "/approvals", label: "Approvals", icon: ShieldCheck, module: "approvals" },
    { to: "/notifications", label: "Notifications", icon: Bell, module: "notifications" },
    { to: "/import", label: "Bulk Import", icon: Upload, module: "employees" },
    { to: "/cp-dedupe", label: "CP Duplicates", icon: GitMerge, module: "cps" },
    { to: "/reviews", label: "Performance Reviews", icon: ClipboardList, module: "employees" },
    { to: "/admin-data", label: "Data & Backup", icon: DatabaseBackup, module: "employees" },
    { to: "/employees", label: "Employees", icon: UserCog, module: "employees" },
    { to: "/masters", label: "Masters", icon: Cog, module: "masters" },
    { to: "/audit", label: "Audit Logs", icon: ScrollText, module: "audit" },
  ]},
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const visible = (module) => {
    if (!user) return false;
    if (user.role_config?.all) return true;
    return (user.role_config?.modules || []).includes(module);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-64"} bg-sidebar text-sidebar-foreground border-r border-border transition-all duration-200 flex flex-col`}
             data-testid="app-sidebar">
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-serif-display text-lg font-semibold tracking-tight">GRAM</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Strategic Advisors</div>
            </div>
          )}
          <button className="text-muted-foreground hover:text-foreground" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle-btn">
            {collapsed ? <ChevronsRight size={16}/> : <ChevronsLeft size={16}/>}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.map((g) => {
            const items = g.items.filter(i => visible(i.module));
            if (items.length === 0) return null;
            return (
              <div key={g.group} className="mb-4">
                {!collapsed && <div className="px-4 mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{g.group}</div>}
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink key={it.to} to={it.to} data-testid={`sidebar-nav-${it.module}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent/10 border-l-2 ${isActive ? "border-accent bg-accent/10 text-foreground font-medium" : "border-transparent text-muted-foreground"}`}>
                      <Icon size={16} />
                      {!collapsed && <span>{it.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          {!collapsed && (
            <div className="mb-2">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{user?.role_config?.label || user?.role}</div>
            </div>
          )}
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn">
            <LogOut size={14} className="mr-2"/> {!collapsed && "Sign out"}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/70 backdrop-blur flex items-center gap-4 px-6 sticky top-0 z-20">
          <div className="relative flex-1 max-w-xl">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search leads, clients, CPs, projects…"
                   className="pl-9 h-9" data-testid="global-search-input"/>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden md:inline text-[11px] uppercase tracking-widest text-muted-foreground">Division:</span>
            <span className="hidden md:inline text-xs px-2 py-1 rounded-md bg-secondary text-secondary-foreground">{user?.division || "All"}</span>
            <button className="text-muted-foreground hover:text-foreground" onClick={() => nav("/notifications")} data-testid="topbar-notifications-btn">
              <Bell size={16}/>
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) { window.location.href = "/login"; return null; }
  return children;
}
